import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import API_BASE_URL from '../../services/api';
import { Button } from '../../components/ui/button';
import axios from 'axios';

const emptyForm = {
  slug: '', name: '', role: '', location: '', joined_year: 2025,
  image: '', images: '', videos: '', bio: '',
  specialties: '', achievements: '', vision: '',
  social_email: '', social_linkedin: '', social_twitter: '',
  order: 0, active: true
};

const TeamManager = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingSlug, setEditingSlug] = useState('');

  const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };

  const fetchList = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/team`);
      const j = await r.json();
      setList(j.team || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchList(); }, []);

  const toArray = (s) => (s ? s.split('\n').map(x => x.trim()).filter(Boolean) : []);

  const save = async () => {
    setLoading(true);
    try {
      const payload = {
        id: crypto.randomUUID(),
        slug: form.slug.trim(),
        name: form.name.trim(),
        role: form.role,
        location: form.location,
        joined_year: Number(form.joined_year) || 2025,
        image: form.image,
        images: toArray(form.images),
        videos: toArray(form.videos),
        bio: form.bio,
        specialties: toArray(form.specialties),
        achievements: toArray(form.achievements),
        vision: toArray(form.vision),
        social: {
          email: form.social_email || undefined,
          linkedin: form.social_linkedin || undefined,
          twitter: form.social_twitter || undefined
        },
        order: Number(form.order) || 0,
        active: !!form.active
      };
      const r = await fetch(`${API_BASE_URL}/api/admin/team`, { method: 'POST', headers, body: JSON.stringify(payload) });
      if (!r.ok) throw new Error('Erreur sauvegarde');
      await fetchList();
      setForm(emptyForm); setEditingSlug('');
      alert('Profil sauvegardé');
    } catch (e) { alert(e.message); }
    finally { setLoading(false); }
  };

  const edit = (m) => {
    setEditingSlug(m.slug);
    setForm({
      slug: m.slug, name: m.name, role: m.role || '', location: m.location || '', joined_year: m.joined_year || 2025,
      image: m.image || '', images: (m.images || []).join('\n'), videos: (m.videos || []).join('\n'), bio: m.bio || '',
      specialties: (m.specialties || []).join('\n'), achievements: (m.achievements || []).join('\n'), vision: (m.vision || []).join('\n'),
      social_email: m.social?.email || '', social_linkedin: m.social?.linkedin || '', social_twitter: m.social?.twitter || '',
      order: m.order || 0, active: !!m.active
    });
  };

  const remove = async (slug) => {
    if (!confirm('Supprimer ce profil ?')) return;
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/team/${slug}`, { method: 'DELETE', headers });
      if (!r.ok) throw new Error('Erreur suppression');
      await fetchList();
    } catch (e) { alert(e.message); }
  };

  // Upload helpers (utilise POST /api/media/upload)
  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await axios.post(`${API_BASE_URL}/api/media/upload`, fd, {
      headers: { Authorization: token ? `Bearer ${token}` : undefined }
    });
    return res.data; // { success, url }
  };

  const addImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const up = await uploadFile(file);
      if (up?.url) {
        const list = form.images ? form.images.split('\n').filter(Boolean) : [];
        list.push(up.url);
        setForm(prev => ({ ...prev, images: list.join('\n') }));
      }
    } catch (err) {
      alert('Erreur upload image: ' + (err?.response?.data?.detail || err.message));
    } finally {
      e.target.value = '';
    }
  };

  const addVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const up = await uploadFile(file);
      if (up?.url) {
        const list = form.videos ? form.videos.split('\n').filter(Boolean) : [];
        list.push(up.url);
        setForm(prev => ({ ...prev, videos: list.join('\n') }));
      }
    } catch (err) {
      alert('Erreur upload vidéo: ' + (err?.response?.data?.detail || err.message));
    } finally {
      e.target.value = '';
    }
  };

  const Field = ({ label, children }) => (
    <div className="mb-3">
      <div className="text-sm font-medium mb-1">{label}</div>
      {children}
    </div>
  );

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">Gestion de l'équipe</h1>

      {/* Formulaire */}
      <div className="border rounded-lg p-4 mb-8">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Slug (unique)">
            <input className="w-full border rounded px-3 py-2" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="ex: kulina" />
          </Field>
          <Field label="Nom">
            <input className="w-full border rounded px-3 py-2" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="KULINA (Abakar)" />
          </Field>
          <Field label="Rôle">
            <input className="w-full border rounded px-3 py-2" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} />
          </Field>
          <Field label="Localisation">
            <input className="w-full border rounded px-3 py-2" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} />
          </Field>
          <Field label="Année">
            <input className="w-full border rounded px-3 py-2" type="number" value={form.joined_year} onChange={e => setForm({ ...form, joined_year: e.target.value })} />
          </Field>
          <Field label="Image principale (URL)">
            <input className="w-full border rounded px-3 py-2" value={form.image} onChange={e => setForm({ ...form, image: e.target.value })} />
            <div className="text-xs text-muted-foreground mt-1">ou uploader:
              <input type="file" accept="image/*" className="mt-1 block text-sm" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return; 
                try {
                  const up = await uploadFile(file);
                  if (up?.url) setForm(prev => ({ ...prev, image: up.url }));
                } catch (err) { alert('Erreur upload image: ' + (err?.response?.data?.detail || err.message)); }
                finally { e.target.value = ''; }
              }} />
            </div>
          </Field>
        </div>
        <Field label="Galerie photos (une URL par ligne)">
          <textarea className="w-full border rounded px-3 py-2 h-24" value={form.images} onChange={e => setForm({ ...form, images: e.target.value })} />
          <div className="text-xs text-muted-foreground mt-1">ou uploader des images:
            <input type="file" multiple accept="image/*" className="mt-1 block text-sm" onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              for (const f of files) { await addImageUpload({ target: { files: [f] } }); }
              e.target.value = '';
            }} />
          </div>
        </Field>
        <Field label="Vidéos (URL mp4 ou YouTube/Vimeo, une par ligne)">
          <textarea className="w-full border rounded px-3 py-2 h-24" value={form.videos} onChange={e => setForm({ ...form, videos: e.target.value })} />
          <div className="text-xs text-muted-foreground mt-1">ou uploader une vidéo (mp4):
            <input type="file" accept="video/mp4,video/*" className="mt-1 block text-sm" onChange={addVideoUpload} />
          </div>
        </Field>
        <Field label="Bio">
          <textarea className="w-full border rounded px-3 py-2 h-28" value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} />
        </Field>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Spécialités (une par ligne)">
            <textarea className="w-full border rounded px-3 py-2 h-28" value={form.specialties} onChange={e => setForm({ ...form, specialties: e.target.value })} />
          </Field>
          <Field label="Réalisations (une par ligne)">
            <textarea className="w-full border rounded px-3 py-2 h-28" value={form.achievements} onChange={e => setForm({ ...form, achievements: e.target.value })} />
          </Field>
          <Field label="Vision (une par ligne)">
            <textarea className="w-full border rounded px-3 py-2 h-28" value={form.vision} onChange={e => setForm({ ...form, vision: e.target.value })} />
          </Field>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Email">
            <input className="w-full border rounded px-3 py-2" value={form.social_email} onChange={e => setForm({ ...form, social_email: e.target.value })} />
          </Field>
          <Field label="LinkedIn">
            <input className="w-full border rounded px-3 py-2" value={form.social_linkedin} onChange={e => setForm({ ...form, social_linkedin: e.target.value })} />
          </Field>
          <Field label="Twitter">
            <input className="w-full border rounded px-3 py-2" value={form.social_twitter} onChange={e => setForm({ ...form, social_twitter: e.target.value })} />
          </Field>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Ordre">
            <input className="w-full border rounded px-3 py-2" type="number" value={form.order} onChange={e => setForm({ ...form, order: e.target.value })} />
          </Field>
          <Field label="Actif">
            <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} />
          </Field>
        </div>
        <div className="flex justify-end gap-3 mt-3">
          {editingSlug && (
            <Button variant="outline" onClick={() => { setForm(emptyForm); setEditingSlug(''); }}>Annuler</Button>
          )}
          <Button onClick={save} disabled={loading}>{loading ? 'Sauvegarde…' : 'Sauvegarder'}</Button>
        </div>
      </div>

      {/* Liste */}
      <div className="space-y-2">
        {list.map((m) => (
          <div key={m.id} className="border rounded p-3 flex items-center justify-between">
            <div>
              <div className="font-semibold">{m.name} <span className="text-muted-foreground">({m.slug})</span></div>
              <div className="text-sm text-muted-foreground">{m.role}</div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => edit(m)}>Éditer</Button>
              <Button variant="destructive" onClick={() => remove(m.slug)}>Supprimer</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TeamManager;